// lib
import { useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'

// styles
import style from '@aytm/app/stats/components/transition-panel/styles/TransitionPanel.module.scss'

// How long a panel takes to open or shut. Shared, because a caller that animates alongside one --
// dropping a shadow, unmounting a body once it is off screen -- has to wait exactly as long.
export const TRANSITION_DURATION = 500

const TransitionPanel = ({ children, className = '', in: inProp, timeout = TRANSITION_DURATION, ...panelProps }) => {
  const nodeRef = useRef(null)

  // Keep the CSS transition in lockstep with the Transition timeout so the
  // animation never desyncs from the unmount delay.
  const panelStyle = 'number' == typeof timeout ? { transitionDuration: `${timeout}ms` } : undefined

  // Animate the panel by setting max-height to the measured content height; on
  // expand-complete we drop the cap to `none` so nested rows can grow freely.
  const onEnter = useCallback(() => {
    if (nodeRef.current) nodeRef.current.style.maxHeight = '0px'
  }, [])

  const onEntering = useCallback(() => {
    if (nodeRef.current) nodeRef.current.style.maxHeight = `${nodeRef.current.scrollHeight}px`
  }, [])

  const onEntered = useCallback(() => {
    if (nodeRef.current) nodeRef.current.style.maxHeight = 'none'
  }, [])

  const onExit = useCallback(() => {
    if (nodeRef.current) nodeRef.current.style.maxHeight = `${nodeRef.current.scrollHeight}px`
  }, [])

  const onExiting = useCallback(() => {
    if (!nodeRef.current) return
    void nodeRef.current.offsetHeight // force reflow so the browser registers the start height before collapsing
    nodeRef.current.style.maxHeight = '0px'
  }, [])

  // A panel rendered already-expanded gets no enter transition, so lift the cap directly.
  useEffect(() => {
    if (nodeRef.current && inProp) {
      nodeRef.current.style.maxHeight = 'none'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Transition
      in={inProp}
      timeout={timeout}
      nodeRef={nodeRef}
      onEnter={onEnter}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExiting={onExiting}
    >
      {status => (
        <div
          ref={nodeRef}
          className={`${style['transition-panel']}${status ? ' ' + style['transition-panel-' + status] : ''}${
            className ? ' ' + className : ''
          }`}
          style={panelStyle}
          {...panelProps}
        >
          {'function' == typeof children ? children(status) : children}
        </div>
      )}
    </Transition>
  )
}

TransitionPanel.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  in: PropTypes.bool,
  timeout: PropTypes.oneOfType([PropTypes.number, PropTypes.object])
}

TransitionPanel.displayName = 'TransitionPanel'

export default TransitionPanel
